import { t } from "adapters/i18n";
import type { Dispatch } from "react-redux";
import type { RootState } from "redux/reducers";
import herdService from "services/herdService";
import { getErrorMessage } from "shared/errorUtils";
import {
  exibirCarregando,
  listEnrichedFarmCategories,
  notifyError,
  notifySuccess,
  type CategoryDispatchAction,
} from "../actions";

export const fetchEnrichedFarmCategories =
  (withDeleted?: boolean) =>
  async (
    dispatch: Dispatch<CategoryDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      dispatch(exibirCarregando(true));

      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        throw new Error(t("common:no-farm-selected"));
      }

      const enrichedFarmCategories =
        await herdService.fetchEnrichedFarmCategories(
          fazenda.FazendaSelecionada,
          withDeleted
        );

      dispatch(listEnrichedFarmCategories(enrichedFarmCategories.data));
    } catch (error) {
      dispatch(listEnrichedFarmCategories([]));
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    } finally {
      dispatch(exibirCarregando(false));
    }
  };

export const activateCategory =
  (id: number) => async (dispatch: Dispatch<CategoryDispatchAction>) => {
    try {
      await herdService.activateCategory(id);

      dispatch(
        notifySuccess({
          mensagem: t("animalCategory:activate-category-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const deleteCategory =
  (id: number) => async (dispatch: Dispatch<CategoryDispatchAction>) => {
    try {
      await herdService.deleteCategoryById(id);

      dispatch(
        notifySuccess({
          mensagem: t("animalCategory:delete-category-success"),
        })
      );
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const createCategory =
  (payload: Omit<Parameters<typeof herdService.createCategory>[0], "farmId">) =>
  async (
    dispatch: Dispatch<CategoryDispatchAction>,
    getState: () => RootState
  ) => {
    try {
      const { fazenda } = getState();

      if (fazenda.FazendaSelecionada == null) {
        dispatch(notifyError({ mensagem: t("common:no-farm-selected") }));
        return;
      }

      await herdService.createCategory({
        ...payload,
        farmId: fazenda.FazendaSelecionada,
      });

      dispatch(
        notifySuccess({ mensagem: t("animalCategory:create-category-success") })
      );
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    }
  };

export const updateCategory =
  (
    id: number,
    payload: Omit<Parameters<typeof herdService.updateCategory>[1], "farmId">
  ) =>
  async (dispatch: Dispatch<CategoryDispatchAction>) => {
    try {
      await herdService.updateCategory(id, { ...payload });

      dispatch(
        notifySuccess({ mensagem: t("animalCategory:update-category-success") })
      );
    } catch (error) {
      const message = getErrorMessage(error);
      dispatch(notifyError({ mensagem: message }));
    }
  };
